import { useEffect, useRef, useState, useCallback } from 'react';
import { whatsappApi } from "../../whatsapp/api/whatsapp-api";
import { emailApi } from "../../email/api/email-api";

export type JobStatus = 'idle' | 'processing' | 'completed' | 'failed';

export const useJobProgress = (channel: 'WhatsApp' | 'Correo') => {
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState<JobStatus>('idle');
    const [templateName, setTemplateName] = useState<string | null>(null);
    const [error, setError] = useState<any>(null);
    const completeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const unsubscribeRef = useRef<null | (() => void)>(null);
    const lastProgressRef = useRef<number>(0);
    // Latch para estados terminales: mientras esté activo, ignoramos nuevos ticks (p.ej. no_jobs_found posterior)
    const terminalStateRef = useRef<JobStatus | null>(null);

    const clearCompleteTimeout = () => {
        if (completeTimeoutRef.current) {
            clearTimeout(completeTimeoutRef.current);
            completeTimeoutRef.current = null;
        }
    };

    const clearHideTimeout = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    }, []);

    const dismissJob = useCallback(() => {
        setStatus('idle');
        setProgress(0);
        lastProgressRef.current = 0;
        setTemplateName(null);
        setError(null);
        terminalStateRef.current = null;
        clearHideTimeout();
    }, [clearHideTimeout]);

    const closeStream = () => {
        try {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
        } catch (e) {
            // noop
        } finally {
            unsubscribeRef.current = null;
        }
    };

    const openStream = useCallback(() => {
        // Determinar qué API usar basándose en el parámetro channel
        const api = channel === 'Correo' ? emailApi : whatsappApi;

        // Abre el stream SSE y registra el unsubscribe en la ref
        unsubscribeRef.current = api.getProgressStream(
            (data: any) => {
                const incomingStatus: string | undefined = data?.status;
                // Mapear "progress" del backend a "processing" del frontend
                const mappedStatus = incomingStatus === 'progress' ? 'processing' : incomingStatus;
                const newStatus = (mappedStatus as JobStatus) ?? 'processing';

                // Si ya alcanzamos un estado terminal, ignorar cualquier otro evento
                if (terminalStateRef.current === 'completed' || terminalStateRef.current === 'failed') {
                    return;
                }

                // Mapear nombre de plantilla desde múltiples rutas posibles
                const mappedTemplateName =
                    data?.data?.templatePayload?.name ||
                    data?.data?.baseTemplateUsage?.template?.name ||
                    null;
                if (mappedTemplateName) {
                    setTemplateName(mappedTemplateName);
                }

                if (incomingStatus === 'completed') {
                    const finishedAt = data?.finishedAt;
                    if (finishedAt) {
                        const age = new Date().getTime() - finishedAt;
                        const remainingTime = 60000 - age;
                        if (remainingTime <= 0) {
                            // Si ya pasó más de 1 minuto, lo ocultamos de inmediato
                            dismissJob();
                            return; // No mostramos el 'completed'
                        } else {
                            // Programar ocultamiento
                            clearHideTimeout();
                            hideTimeoutRef.current = setTimeout(() => {
                                dismissJob();
                            }, remainingTime);
                        }
                    }

                    // Usar el progreso real del stream en lugar de forzar 100%
                    const incomingProgress = typeof data?.progress === 'number' ? data.progress : lastProgressRef.current;
                    const clamped = Math.max(0, Math.min(100, incomingProgress));
                    const nextProgress = Math.max(lastProgressRef.current, clamped);
                    setProgress(nextProgress);
                    lastProgressRef.current = nextProgress;
                    setStatus('completed');
                    terminalStateRef.current = 'completed';
                    // Persistencia visual: retrasar el cierre 1.5s
                    clearCompleteTimeout();
                    completeTimeoutRef.current = setTimeout(() => {
                        closeStream();
                        // Mantener UI en 'completed' hasta que el usuario la descarte manualmente (o el timeout actúe)
                        terminalStateRef.current = null; // liberar el latch tras cerrar el stream
                    }, 1500);
                    return;
                }

                if (incomingStatus === 'no_jobs_found') {
                    // Cerrar y limpiar a estado silencioso
                    // Si ya estuvimos en estado terminal, ignorar este rebote
                    if (!terminalStateRef.current) {
                        closeStream();
                        setStatus('idle');
                        setProgress(0);
                        lastProgressRef.current = 0;
                        setTemplateName(null);
                        setError(null);
                    }
                    return;
                }

                // Para otros estados, actualizar normalmente
                const currentProgress = typeof data?.progress === 'number' ? data.progress : lastProgressRef.current;
                // Asegurar 0-100 y no decrecer visualmente durante el mismo job
                const clamped = Math.max(0, Math.min(100, currentProgress));
                const nextProgress = Math.max(lastProgressRef.current, clamped);
                lastProgressRef.current = nextProgress;
                setProgress(nextProgress);
                setStatus(newStatus);

                if (incomingStatus === 'failed' || newStatus === 'failed') {
                    // En fallo, cerrar inmediatamente
                    terminalStateRef.current = 'failed';
                    setError(data?.errorMessage || data?.error || 'Ocurrió un error inesperado.');
                    closeStream();
                    
                    const finishedAt = data?.finishedAt;
                    if (finishedAt) {
                        const age = new Date().getTime() - finishedAt;
                        const remainingTime = 60000 - age;
                        if (remainingTime <= 0) {
                            dismissJob();
                        } else {
                            clearHideTimeout();
                            hideTimeoutRef.current = setTimeout(() => {
                                dismissJob();
                            }, remainingTime);
                        }
                    }
                }
            },
            (err) => {
                console.error("Error en SSE de progreso:", err);
                setError("Se perdió la conexión con el monitor de envíos.");
                setStatus('failed');
            }
        );
    }, [channel, dismissJob, clearHideTimeout]);

    // Suscripción inicial (opcional): monitorea si ya hay un job activo
    useEffect(() => {
        openStream();
        return () => {
            clearCompleteTimeout();
            clearHideTimeout();
            closeStream();
        };
    }, [openStream, clearHideTimeout]);

    const reconnect = useCallback((initialData?: { templateName?: string }) => {
        // Cierra cualquier stream previo
        clearCompleteTimeout();
        clearHideTimeout();
        closeStream();

        // Inicializa UI inmediatamente para evitar parpadeo
        setStatus('processing');
        setProgress(0);
        lastProgressRef.current = 0;
        setError(null);
        terminalStateRef.current = null;
        if (initialData?.templateName) {
            setTemplateName(initialData.templateName);
        }

        // Abre nuevo stream
        openStream();
    }, [openStream, clearHideTimeout]);

    return { progress, status, templateName, error, dismissJob, reconnect };
};
